<template>
  <SFItemsSlider
    with-arrows
    mode="horizontal"
    data-testid="product-thumbnails"
    :slider-tabindex="-1"
  >
    <template #header></template>
    <div class="flex flex-col-reverse md:flex-col">
      <div class="mt-4 flex justify-between px-4 md:mb-4 md:mt-0 md:px-0">
        <div class="flex gap-2 text-md">
          <span class="font-semibold">
            {{ $t('product_attribute.color') }}:
          </span>
          <span>{{ label }}</span>
        </div>
      </div>
      <div class="my-0.5 flex w-full gap-x-0.5 md:gap-4 md:py-1.5">
        <SFLink
          v-for="sibling in siblings"
          :key="sibling.id"
          class="relative size-20 shrink-0 overflow-hidden border-2 transition-all supports-hover:hover:border-black md:size-16 md:rounded-lg"
          :class="{
            'pointer-events-none':
              sibling.isSoldOut || sibling.id === product.id,
            'border-white': sibling.id !== product.id && !sibling.isSoldOut,
            'border-anthracite-500 text-anthracite-800 -outline-offset-4 ':
              sibling.id === product.id,
            'bg-gray-100 text-gray-300 ': sibling.id !== product.id,
            'border-gray-200': sibling.isSoldOut && sibling.id !== product.id,
          }"
          :to="
            getProductDetailRoute(
              sibling.id,
              sibling.name,
              formatColors(sibling.colors),
              sibling.brand,
            )
          "
          @mouseenter="setHoveredLabel(sibling)"
          @mouseleave="setHoveredLabel()"
          @click="trackSiblingClick(sibling)"
        >
          <div
            v-if="sibling.isSoldOut"
            class="absolute left-0 size-full rounded-md diagonal-strikethrough"
          />

          <SFProductImage
            v-if="sibling.image"
            :alt="siblingAltText(sibling)"
            sizes="180px"
            class="h-full"
            :class="{
              'opacity-20': sibling.isSoldOut && sibling.id !== product.id,
            }"
            :image="sibling.image"
            :aspect-ratio="[1, 1]"
          />
        </SFLink>
      </div>
    </div>

    <template
      #arrows="{ isPrevEnabled, isNextEnabled, prev, next, isScrollable }"
    >
      <div
        class="absolute right-0 top-0 flex gap-0.5 max-md:hidden"
        :class="{ hidden: !isScrollable }"
      >
        <SFButton
          class="!size-6 rounded-l-full first:!p-0.5"
          :disabled="!isPrevEnabled"
          :aria-label="$t('slider.got_to_previous_item')"
          variant="slider"
          @click="prev()"
        >
          <IconChevronLeft class="size-4" />
        </SFButton>
        <SFButton
          class="!size-6 rounded-r-full last:!p-0.5"
          :aria-label="$t('slider.got_to_next_item')"
          :disabled="!isNextEnabled"
          variant="slider"
          @click="next()"
        >
          <IconChevronRight class="size-4" />
        </SFButton>
      </div>
    </template>
  </SFItemsSlider>
</template>

<script setup lang="ts">
import type { Product } from '@scayle/storefront-nuxt'
import { computed, ref } from 'vue'
import SFProductImage from './SFProductImage.vue'
import { SFButton, SFItemsSlider, SFLink } from '#storefront-ui/components'
import {
  useProductBaseInfo,
  useRouteHelpers,
  useTrackingEvents,
} from '~/composables'
import type { ProductSibling } from '~/types/siblings'
import { useI18n } from '#i18n'
import { formatColors } from '~/utils'

const { product } = defineProps<{ product: Product }>()

const { getProductDetailRoute } = useRouteHelpers()
const { siblings } = useProductBaseInfo(() => product)

const { t } = useI18n()

const hoveredColorLabel = ref()

const setHoveredLabel = (sibling?: ProductSibling) => {
  if (!sibling) {
    hoveredColorLabel.value = undefined
    return
  }

  hoveredColorLabel.value =
    sibling.colors?.[0]?.label?.toLowerCase() ||
    t('sibling_selection.sibling_error')
}

const label = computed(() => {
  const firstSiblingColors = siblings.value[0]?.colors

  if (!firstSiblingColors?.length) {
    return t('sibling_selection.sibling_error')
  }

  return hoveredColorLabel.value || firstSiblingColors[0].label.toLowerCase()
})

const siblingAltText = (sibling: ProductSibling) => {
  const placeholder = {
    alt: t('product_image.alt', {
      productName: sibling.name,
      colors: formatColors(sibling.colors),
      brand: sibling.brand,
    }),
    selected:
      product.id === sibling.id
        ? t('sibling_selection.selected')
        : t('sibling_selection.unselected'),
  }
  return sibling.isSoldOut
    ? t('sibling_selection.a11ly.sold_out_alt_text', placeholder)
    : t('sibling_selection.a11ly.alt_text', placeholder)
}

const trackSiblingClick = (sibling: ProductSibling) => {
  const siblingsProduct = product.siblings?.find(
    (product) => product.id === sibling.id,
  )
  if (!siblingsProduct) {
    return
  }
  try {
    const trackingEvents = useTrackingEvents()

    // Track view_item for color change
    trackingEvents.trackViewItem({
      product: siblingsProduct,
      quantity: 1,
    })
  } catch (e) {
    console.error(e)
  }
}
</script>
